import { IAccount } from '@medely/types';
import { isSaasAccount, isAgencyAccount } from './account';
import { hasPushTokens } from './pushNotificationHelpers';

export enum NotificationKey {
  SMS_NOTIFICATION = 'sms_notification',
  CLOCK_IN_OUT = 'clock_in_out',
  PER_DIEM_REC = 'per_diem_rec',
  SMS_NOTIFICATION_BULK = 'sms_notification_bulk',
  LOCAL_ASSIGNMENT_SMS_NOTIFICATION = 'local_assignment_sms_notification',
  LOCAL_ASSIGNMENT_EMAIL_NOTIFICATION = 'local_assignment_email_notification',
  ASSIGNMENT_SMS_NOTIFICATION = 'assignment_sms_notification',
  ASSIGNMENT_REC = 'assignment_rec',
  NEW_REQUIREMENT_FOR_SAAS_PRO = 'new_requirement_for_saas_pro',
  PAYOUT_NOTIFICATIONS = 'payout_notifications',
}

export enum NotificationType {
  SMS = 'sms',
  EMAIL = 'email',
  PUSH = 'push',
}

export const NOTIFICATION_SWITCH_LABEL = {
  [NotificationType.SMS]: 'Text message',
  [NotificationType.EMAIL]: 'Email',
  [NotificationType.PUSH]: 'Push Notifications',
};

export type NotificationItem = {
  type: NotificationType;
  key: string;
};

export type NotificationSection = {
  label: string;
  items: NotificationItem[];
  description?: string;
};

export const shiftPaidNotificationSection: NotificationSection = {
  label: 'Shift paid',
  description: 'Receive an alert when Medely sends payment for your shifts',
  items: [
    {
      type: NotificationType.SMS,
      key: NotificationKey.PAYOUT_NOTIFICATIONS,
    },
    {
      type: NotificationType.EMAIL,
      key: NotificationKey.PAYOUT_NOTIFICATIONS,
    },
    {
      type: NotificationType.PUSH,
      key: NotificationKey.PAYOUT_NOTIFICATIONS,
    },
  ],
};

export const pushNotificationSection: NotificationSection = {
  label: 'Clock In/Out Notifications',
  description: "Get notifications when it's time to clock in or out",
  items: [
    {
      type: NotificationType.PUSH,
      key: NotificationKey.CLOCK_IN_OUT,
    },
  ],
};

export const notificationSections: NotificationSection[] = [
  {
    label: 'Per diem shifts',
    description: 'Receive notifications when a new per diem shift that matches your profile.',
    items: [
      {
        type: NotificationType.SMS,
        key: NotificationKey.SMS_NOTIFICATION,
      },
      {
        type: NotificationType.EMAIL,
        key: NotificationKey.PER_DIEM_REC,
      },
    ],
  },
  {
    label: 'Daily per diem updates',
    description: 'Get a daily summary of new per diem shifts that may be a good fit for you.',
    items: [
      {
        type: NotificationType.SMS,
        key: NotificationKey.SMS_NOTIFICATION_BULK,
      },
    ],
  },
  {
    label: 'Local assignments',
    description: 'Receive notifications for new local assignments.',
    items: [
      {
        type: NotificationType.SMS,
        key: NotificationKey.LOCAL_ASSIGNMENT_SMS_NOTIFICATION,
      },
      {
        type: NotificationType.EMAIL,
        key: NotificationKey.LOCAL_ASSIGNMENT_EMAIL_NOTIFICATION,
      },
    ],
  },
  {
    label: 'Travel assignments',
    description: 'Get notified about new travel assignment opportunities that match your profile.',
    items: [
      {
        type: NotificationType.SMS,
        key: NotificationKey.ASSIGNMENT_SMS_NOTIFICATION,
      },
      {
        type: NotificationType.EMAIL,
        key: NotificationKey.ASSIGNMENT_REC,
      },
    ],
  },
];

export const saasNotificationSections: NotificationSection[] = [
  {
    label: 'Compliance',
    description: 'Receive emails to stay compliant with your facility.',
    items: [
      {
        type: NotificationType.EMAIL,
        key: NotificationKey.NEW_REQUIREMENT_FOR_SAAS_PRO,
      },
    ],
  },
];

export const getNotificationSections = (
  account: IAccount,
  shiftPaidEnabled: boolean,
  localAssignmentEnabled = false,
): NotificationSection[] => {
  const displayPush = hasPushTokens(account);

  if (isSaasAccount(account)) {
    return saasNotificationSections;
  }

  const removeNotificationSectionBasedOnCondition = (
    notification: NotificationSection,
    labelToRemove: string,
    condition: boolean,
  ) => {
    return !(notification.label === labelToRemove && condition);
  };

  const insertSectionBefore = (
    list: NotificationSection[],
    sectionToInsert: NotificationSection,
    label: string,
  ) => {
    const index = list.findIndex((s) => s.label === label);
    if (index > -1) {
      list.splice(index, 0, sectionToInsert);
    } else {
      list.push(sectionToInsert);
    }
  };

  const removeNotificationItemBasedOnCondition = (
    notification: NotificationSection,
    labelToUpdate: string,
    keyToRemove: NotificationKey,
    condition: boolean,
  ) => {
    if (notification.label !== labelToUpdate || !condition) {
      return notification;
    }

    return {
      ...notification,
      items: notification.items.filter((item) => item.key !== keyToRemove),
    };
  };

  const filteredSections = (
    displayPush ? [pushNotificationSection, ...notificationSections] : [...notificationSections]
  ).filter((notification) =>
    removeNotificationSectionBasedOnCondition(
      notification,
      'Travel assignments',
      isAgencyAccount(account),
    ),
  );

  if (shiftPaidEnabled && !isAgencyAccount(account)) {
    insertSectionBefore(filteredSections, shiftPaidNotificationSection, 'Travel assignments');
  }

  return filteredSections.map((notification) =>
    removeNotificationItemBasedOnCondition(
      notification,
      'Local assignments',
      NotificationKey.LOCAL_ASSIGNMENT_EMAIL_NOTIFICATION,
      !localAssignmentEnabled,
    ),
  );
};
